import React, { memo, useContext } from 'react';
import { SidebarNextContext } from '../SidebarNext/SidebarNextContext';
import { useFocusRing } from '../providers/useFocusRing/useFocusRing';

import { dataHooks, skins } from './constants';
import { classes, st } from './SidebarItemNext.st.css';
import { Transition } from '../Transition';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

type HTMLTagName = keyof HTMLElementTagNameMap;

type AsButtonProps = {
  as?: 'button';
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type AsAnchorProps = {
  as?: 'a';
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

type AsOtherTagProps = {
  as?: Exclude<HTMLTagName, 'a' | 'button'>;
} & React.HTMLAttributes<HTMLElement>;

type AsAnyComponentProps = {
  as?: React.ComponentType<any>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  [props: string]: any;
};

// FIXME: this will produce invalid intersections (e.g. `prefix` already is HTML attribute but HTML attributes cannot be `ReactNode`)
type SidebarItemButtonNextProps = (
  | AsButtonProps
  | AsAnchorProps
  | AsOtherTagProps
  | AsAnyComponentProps
) & {
  children?: React.ReactNode;
  dataHook?: string;
  className?: string;
  itemKey?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  role?: string;
  disabled?: boolean;
  isInQuickNavigation?: boolean; // FIXME: deprecate, this was not in types anyway
  onClick?: React.MouseEventHandler<HTMLElement>;
  depth?: 'first' | 'second' | 'third';
  depthDivider?: 'top' | 'full' | 'bottom';
  minimizedTooltipContent?: React.ReactNode;
  skin?: 'dark' | 'light' | 'neutral';
  minimized?: boolean;
};

const SidebarItemButtonNext = memo<SidebarItemButtonNextProps>(props => {
  const context = useContext(SidebarNextContext);
  const {
    skin: contextSkin,
    level,
    selectedPath,
    inert,
    parent,
    closeQuickNav,
    legacy,
    minimized: contextMinimized,
    isFirstLevelWithPrefix,
  } = context;

  const {
    as = 'button',
    role = as === 'a' ? 'link' : 'button', // FIXME: check original code, this is weird
    // @ts-ignore
    href, // FIXME: from intersection
    dataHook,
    children,
    disabled,
    onClick,
    className,
    itemKey,
    suffix,
    prefix,
    isInQuickNavigation,
    minimizedTooltipContent,
    skin: skinProp,
    minimized: minimizedProp,
    ...asComponentProps
  } = props;

  // An explicit `skin` prop overrides the skin provided by the parent
  // SidebarNext via context (e.g. when the item is rendered across a
  // bundle/composition boundary where the context does not resolve).
  const skin = skinProp ?? contextSkin;
  // Same escape hatch for the collapsed state: a consumer bundling its own
  // copy of the design system subscribes to a different context object than
  // the host's SidebarNext, so it can never read the context value.
  const minimized = minimizedProp ?? contextMinimized;

  const { isFocusVisible, focusProps } = useFocusRing();

  const Component = as as React.ComponentType<any>;
  const selected =
    !!selectedPath?.length && selectedPath[selectedPath.length - 1] === itemKey;
  const htmlHref = disabled ? undefined : href;
  const tabIndex = inert ? -1 : 0;
  const textSize = level === 1 ? 'small' : 'tiny';

  const inContextMenu = parent === 'inContextMenu';

  const buttonClassName = st(
    classes.root,
    {
      selected,
      disabled,
      skin,
      inContextMenu,
      hasPrefix: !!prefix,
      isFirstLevelWithPrefix,
      level,
      legacy,
      isFocusVisible,
      minimized,
    },
    className,
  );

  const handleClick = (
    event:
      | React.KeyboardEvent<HTMLDivElement>
      | React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!disabled) {
      closeQuickNav?.();
      // @ts-ignore
      onClick?.(event, itemKey); // FIXME: itemKey was not typed in props
    }
  };

  // Support accessibility for keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick?.(event);
    }
  };

  const tooltipVisible = minimized && !!minimizedTooltipContent;

  return (
    <div
      className={st(classes.tooltipWrapper)}
      {...focusProps}
      tabIndex={tabIndex}
      onKeyDown={handleKeyDown}
    >
      <Tooltip
        placement="right"
        content={minimizedTooltipContent}
        disabled={!tooltipVisible}
        className={classes.tooltip}
      >
        <Component
          tabIndex={-1}
          className={buttonClassName}
          data-hook={dataHook}
          type="button"
          role={role}
          href={htmlHref}
          disabled={href ? false : disabled}
          onClick={handleClick}
          aria-current={selected ? 'page' : undefined} // FIXME: no longer correct
          data-selected={selected}
          data-disabled={disabled}
          data-text-size={textSize}
          data-skin={skin}
          data-minimized={minimized}
          {...asComponentProps}
        >
          {level === 3 && !inContextMenu && <hr className={classes.divider} />}
          {prefix && (
            <span
              className={classes.prefix}
              data-hook={dataHooks.prefix}
              aria-hidden
            >
              {prefix}
            </span>
          )}
          <Transition
            className={classes.textWrapper}
            show={!minimized}
            exitAnimation={{
              fadeOut: {
                duration: 'fast01',
                easing: 'exitEasing',
              },
            }}
          >
            <Text
              dataHook={dataHooks.title}
              className={classes.text}
              size={textSize}
              weight="normal"
              secondary={skin === skins.light}
              light={skin === skins.dark}
              skin={disabled ? 'disabled' : 'standard'}
              showTooltip={false}
            >
              {children}
            </Text>
          </Transition>
          {!disabled && suffix && !minimized && (
            <div className={classes.suffixContainer} aria-hidden>
              {Array.isArray(suffix)
                ? suffix.slice(0, 2).map(renderSuffix)
                : renderSuffix(suffix, 0)}
            </div>
          )}
        </Component>
      </Tooltip>
    </div>
  );
});

const renderSuffix = (suffix: React.ReactNode, index: number) => (
  <span
    key={`${dataHooks.suffix}-${index}`}
    data-hook={dataHooks.suffix}
    className={classes.suffix}
  >
    {suffix}
  </span>
);

SidebarItemButtonNext.displayName = 'SidebarItemButtonNext';

export { SidebarItemButtonNext };
